import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon } from 'lucide-react';
import { BreastDiagramSelector, BreastSide } from './BreastDiagramSelector';
import { getText, isRtl, type Lang } from './translations';
export function LocationQuestionSlide({
  onSelect,
  onBack,
  lang = 'en' as Lang
}: {
  onSelect: (option: string) => void;
  onBack: () => void;
  lang?: Lang;
}) {
  const rtl = isRtl(lang);
  const [selectedSide, setSelectedSide] = useState<BreastSide>(null);
  const handleDiagramSelect = (side: 'left' | 'right') => {
    if (selectedSide === 'both') {
      setSelectedSide(side === 'left' ? 'right' : 'left');
    } else if (selectedSide === side) {
      setSelectedSide(null);
    } else if (selectedSide !== null) {
      setSelectedSide('both');
    } else {
      setSelectedSide(side);
    }
  };
  const handleOptionClick = (option: string) => {
    if (option === getText('leftBreast', lang)) setSelectedSide('left');else if (option === getText('rightBreast', lang)) setSelectedSide('right');else if (option === getText('bothBreasts', lang)) setSelectedSide('both');else setSelectedSide(null);
    setTimeout(() => onSelect(option), 300);
  };
  const showContinue = selectedSide !== null;
  return <div className="flex flex-col h-full px-6 py-8 relative z-10 bg-white" dir={rtl ? 'rtl' : 'ltr'} data-id="element-450">
      {/* Blurry centered background image — atmospheric, no frame */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none flex items-center justify-center" data-id="element-451">
        <motion.img initial={{
        opacity: 0
      }} animate={{
        opacity: 0.25
      }} transition={{
        duration: 1.2
      }} src="https://images.unsplash.com/photo-1516574187841-cb9cc2ca948b?q=80&w=1000&auto=format&fit=crop" className="w-[120%] h-[70%] object-cover object-center blur-sm" style={{
        WebkitMaskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)',
        maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)'
      }} alt="" data-id="element-452" />
      </div>

      <div className="flex items-center justify-between mb-6 relative z-10" data-id="element-453">
        <button onClick={onBack} className="p-2 -ml-2 text-slate-400 hover:text-stone-600 transition-colors bg-white/80 backdrop-blur-sm rounded-full shadow-sm border border-stone-100" aria-label="Go back" data-id="element-454">
          <ArrowLeftIcon className={`w-5 h-5 ${rtl ? 'rotate-180' : ''}`} data-id="element-455" />
        </button>
      </div>

      <div className="flex-1 flex flex-col max-w-sm mx-auto w-full overflow-y-auto no-scrollbar pb-20 relative z-10" data-id="element-456">
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mb-6" data-id="element-457">
          <h2 className="text-2xl font-semibold text-slate-900 leading-tight" data-id="element-458">
            {getText('locationQuestion', lang)}
          </h2>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.15
      }} className="mb-6" data-id="element-459">
          <BreastDiagramSelector selected={selectedSide} onSelect={handleDiagramSelect} data-id="element-460" />
        </motion.div>

        <div className="space-y-3" data-id="element-461">
          {[getText('leftBreast', lang), getText('rightBreast', lang), getText('bothBreasts', lang), getText('notSure', lang)].map((option, index) => {
          const isSelected = option === getText('leftBreast', lang) && selectedSide === 'left' || option === getText('rightBreast', lang) && selectedSide === 'right' || option === getText('bothBreasts', lang) && selectedSide === 'both' || option === getText('notSure', lang) && selectedSide === null && showContinue === false;
          return <motion.button key={option} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2 + index * 0.1
          }} onClick={() => handleOptionClick(option)} className={`w-full text-left p-4 rounded-xl border shadow-sm transition-all duration-200 active:scale-[0.98] ${isSelected ? 'border-nhs-blue bg-nhs-blue/10 ring-1 ring-nhs-blue' : 'border-stone-200 bg-white hover:border-nhs-blue/50'}`} data-id="element-462">
                <span className={`font-medium text-lg ${isSelected ? 'text-nhs-blue' : 'text-stone-700'}`} data-id="element-463">
                  {option}
                </span>
              </motion.button>;
        })}
        </div>
      </div>

      {/* Floating Continue Button if diagram was used */}
      {showContinue && <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="absolute bottom-6 left-6 right-6" data-id="element-464">
          <button onClick={() => {
        const val = selectedSide === 'left' ? getText('leftBreast', lang) : selectedSide === 'right' ? getText('rightBreast', lang) : getText('bothBreasts', lang);
        onSelect(val);
      }} className="w-full py-4 bg-nhs-blue text-white rounded-xl font-semibold text-lg flex items-center justify-center hover:bg-nhs-darkblue shadow-lg shadow-nhs-blue/20 active:scale-[0.98]" data-id="element-465">
            {getText('continue', lang)}
          </button>
        </motion.div>}
    </div>;
}